/**
 * <google-pass-schematic> — a SCHEMATIC of the Google Wallet event ticket a
 * ticket produces, mirroring what the composer
 * (firebase/src/events/orders/deliveries/walletGoogle.api.ts)
 * ships, in the same order top to bottom as the Apple schematic beside it:
 * brand, event, DATE | VENUE, TICKET | TICKET NO., the barcode — one wallet
 * design, two vendors, and the same seven elements the in-app card shows.
 *
 * EVERY ROW IS THE CARD'S ROW, IN THE CARD'S WORDS. The labels resolve through
 * `documents.ticket.*` — the very keys the stencil card reads — and the date
 * through `formatDateRange` at the card's own options, because the composer
 * sends both as `textModulesData` (the card's label in `header`, the card's
 * value in `body`) rather than leaving Google to word and format its own. That
 * is also why no `venue` object and no `dateTime` pair ship: `EventVenue`
 * documents its `address` as required, and the card shows no address.
 *
 * THE ROWS ARE PINNED, NOT SUGGESTED. The composer sends a
 * `classTemplateInfo.cardTemplateOverride` of two `twoItems` rows, so the grid
 * drawn here is the grid Google renders rather than a legible guess. The one
 * structural difference the override cannot remove is alignment:
 * `CardRowTwoItems` documents `startItem` as left-aligned and `endItem` as
 * right-aligned, where the card hangs both columns left — so the right column
 * is drawn right-aligned here, honestly, rather than shown as the card has it.
 *
 * NO HERO. Google's own brand guidelines place `class.heroImage` "under the
 * data fields of your pass", a position the in-app card has no counterpart
 * for, so no band ships on either shape and none is drawn here.
 *
 * WHAT GOOGLE STILL OWNS: the type scale, the QR plate and its position, and
 * the text colour, which is derived on the device from `hexBackgroundColor`
 * rather than sent — so the ink drawn here is the POLARITY that derivation
 * lands on and nothing finer.
 *
 * ONE VERTICAL RHYTHM, SHARED WITH THE APPLE SCHEMATIC. Three distances and no
 * others: `gap-1` inside a label/value cluster, `gap-3` between cells, `gap-6`
 * between regions and around the card. One value scale across all four cells,
 * unlike Apple's two, because Google sizes every field itself and shows no tier
 * we could mirror.
 *
 * THE LOGO IS THE REAL MARK OR NOTHING, AND WHICH FILE IT IS FOLLOWS THE
 * GROUND'S INK POLARITY. `resolveWalletMarkUri` walks the ladder that polarity
 * selects — on a dark ground the derived
 * `workspaces/{orgId}/brand/wallet-logo.png` first and `logo.{png,jpg,jpeg}`
 * after, on a light ground those three alone, because the derived mark is
 * whitened by contract and reads 1.29:1 there — omitting the key outright when
 * no object answers, leaving Google to draw its own generic placeholder: no
 * pass carries a brand initial or a stand-in disc, so neither does this. The
 * preview draws `walletPassMarkUri`, which reads the same `walletInkPolarity`
 * off the same ground, so an operator who accepted "use the light version" is
 * shown that version on the dark looks and their own file on the light one —
 * each where the pass itself carries it. `<brand-logo>` is not reused for any
 * of it: that component masks the mark into a monochrome silhouette filled
 * with `bg-surface-on`, and a pinned document does not recolour its issuer's
 * mark.
 *
 * THE MARK AND THE NAME ARE ONE FACT, SO ONLY ONE OF THEM RENDERS — and either
 * way this row IS the card's own "Produced by" element, which Google draws at
 * the head because its schema has no position below the barcode. This fork's
 * mark IS its wordmark, so a name line beside it says the same thing twice; the
 * pass agrees, since `wideLogo` is documented as "used in place of the logo in
 * the top left of the card view", the slot that otherwise pairs a square logo
 * WITH the issuer name.
 *
 * BRAND IDENTITY IS LIVE, THE EVENT AND TICKET ARE THE SAMPLE: the issuer name
 * (`brandName`), the mark (`walletPassMarkUri`) and the deployment's own seed
 * (`brandSeedColor`) are read live and never fixtured, because the composer
 * ships `issuerName: brand.name` off the live workspace doc. The event, venue,
 * schedule, ticket type and display id stay the preview sample
 * (preview.state.ts): a preview has no real order behind it.
 *
 * The ground is painted inline — a wallet pass is a document, so its colours
 * are its own and never the host theme's. The pass's own values resolve at the
 * sample's buyer locale; the annotation below the frame is admin chrome in the
 * admin's own UI language.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
// Side-effect: registers <schmancy-qr>.
import "@mhmo91/schmancy/qr";
import { formatDateRange } from "@mhmo91/rx";
import { translate } from "@momo/i18n";
import { contrastRatio, walletPassPalette } from "@momo/ticket-template/wallet";
import {
	DEFAULT_TICKET_TEMPLATE_VARIANT,
	type TicketTemplateVariant,
} from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";
import {
	brandName,
	brandSeedColor,
	walletPassMarkUri,
} from "../workspace/workspace.state.ts";
import { ticketPreviewProps } from "./preview.state.ts";

/**
 * Google's on-device derived foreground, not a value the template sends — the
 * pass carries no text colour at all, so drawing our own brand ink here would
 * promise a tone Google never renders. Google publishes no exact tone, only
 * that it picks one against `hexBackgroundColor`, so the preview shows the
 * POLARITY that pick lands on and nothing finer: neutral white over a dark
 * ground, near-black over a light one, each with a muted tier at the weight
 * Google gives a field label.
 *
 * WHICH POLARITY IS READ OFF THE GROUND, NEVER ASSUMED. A variant whose ground
 * is pinned to a vivid light colour is a light ground, and white on it fails
 * every legibility floor — so the choice is made the way a luminance rule
 * makes it, through `contrastRatio` over the ground the palette actually
 * resolved.
 */
const GOOGLE_DERIVED_INK = {
	light: { value: "#ffffff", muted: "rgba(255,255,255,0.72)" },
	dark: { value: "#000000", muted: "rgba(0,0,0,0.72)" },
} as const;

@schmancyElement("google-pass-schematic")
export class GooglePassSchematic extends LocalizeMixin(SchmancyElement) {
	@property({ attribute: false })
	variant: TicketTemplateVariant = DEFAULT_TICKET_TEMPLATE_VARIANT;

	override render(): TemplateResult {
		const palette = walletPassPalette(brandSeedColor.get(), this.variant);
		const ink =
			contrastRatio(palette.ground, GOOGLE_DERIVED_INK.light.value) >=
			contrastRatio(palette.ground, GOOGLE_DERIVED_INK.dark.value)
				? GOOGLE_DERIVED_INK.light
				: GOOGLE_DERIVED_INK.dark;
		const { locale, event, ticket } = ticketPreviewProps.get();
		// `CardRowTwoItems` documents startItem as left-aligned and endItem as
		// right-aligned, which is the one structural difference from the card
		// (both of whose columns hang left) that no field on either shape moves.
		const field = (
			label: string,
			value: string,
			align: "start" | "end",
			// Only the date cell's value is a RANGE, and at this column width it
			// wraps: left unbalanced it breaks after the en dash, stranding it at the
			// line end where it reads as a truncated string rather than a span.
			// Balanced, the date takes one line and both times stay together on the
			// next. The other three values are single terms with nothing to strand.
			balanceValue = false,
		) => html`
			<div class="grid content-start gap-1 ${align === "end" ? "justify-items-end text-right" : ""}">
				<schmancy-typography
					type="label"
					token="xs"
					transform="uppercase"
					class="tracking-widest"
					style="color:${ink.muted};"
				>
					${label}
				</schmancy-typography>
				<schmancy-typography
					type="title"
					token="sm"
					class=${this.classMap({ "text-balance": balanceValue })}
				>
					${value}
				</schmancy-typography>
			</div>
		`;

		return html`
			<div class="grid w-max max-w-full gap-6 p-6">
				<div
					class="grid w-[340px] max-w-full gap-6 overflow-hidden rounded-2xl py-6 shadow-lg"
					style="background-color:${palette.ground};color:${ink.value};"
				>
					${when(
						walletPassMarkUri(this.variant),
						// The mark is the issuer, not decoration beside a line that names
						// it, so it carries the name as its alt text and nothing renders
						// alongside. Height-bound to the identity row's own rhythm and
						// width-bound to the card, which is the only limit a wordmark of
						// the fork's own proportions needs.
						(logo) => html`
							<img
								src=${logo}
								alt=${brandName.get()}
								class="h-6 w-auto max-w-full justify-self-start object-contain px-4"
							/>
						`,
						() => html`
							<schmancy-typography
								type="label"
								token="sm"
								transform="uppercase"
								class="min-w-0 px-4 tracking-widest"
								style="color:${ink.muted};"
							>
								${brandName.get()}
							</schmancy-typography>
						`,
					)}

					<schmancy-typography
						type="headline"
						token="sm"
						weight="bold"
						maxLines="2"
						class="px-4 leading-tight"
					>
						${event.name}
					</schmancy-typography>

					<div class="grid grid-cols-2 gap-3 px-4">
						${field(
							translate(locale, "documents.ticket.dateLabel"),
							formatDateRange(event.start, event.end, {
								locale: locale === "de" ? "de-DE" : "en-US",
								month: "long",
								timeZone: event.timezone,
							}),
							"start",
							true,
						)}
						${field(
							translate(locale, "documents.ticket.venueLabel"),
							event.venue.name,
							"end",
						)}
						${field(
							translate(locale, "documents.ticket.ticketTypeLabel"),
							ticket.typeName,
							"start",
						)}
						${field(
							translate(locale, "documents.ticket.ticketNumberLabel"),
							ticket.displayId,
							"end",
						)}
					</div>

					<!-- The plate, the black modules and the centring are Google's own
					     chrome, drawn from the barcode value alone. -->
					<div class="grid justify-center px-4">
						<div class="rounded-lg bg-white p-2.5">
							<schmancy-qr
								.value=${ticket.qrToken}
								.size=${112}
								.moduleColor=${"#000000"}
							></schmancy-qr>
						</div>
					</div>
				</div>

				<schmancy-typography
					type="body"
					token="sm"
					class="w-[340px] max-w-full text-surface-onVariant"
				>
					${this.t("admin.ticketTemplates.walletPreview.googleNote")}
				</schmancy-typography>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"google-pass-schematic": GooglePassSchematic;
	}
}
